import {
  BADGE_QUANTITY_TOO_LOW,
  TICKET_NOT_AVAILABLE_ANYMORE,
  TICKET_NOT_AVAILABLE_YET,
  TICKET_NOT_ELIGIBLE,
  TICKET_QUANTITY_TOO_LOW,
} from "@ac-expo/api-types";
import { LocalCartItem, useSyncedCart } from "@ac-expo/hooks";
import { centsToPrice, getTicketPurchaseAllowance } from "@ac-expo/utils";
import { cn } from "@ui/utils";
import { BagIcon, MinusCircleIcon, PlusCircleIcon, TrashIcon } from "../../icons";

type UseSyncedCartReturns = ReturnType<typeof useSyncedCart>;

function TicketsCart(props: UseSyncedCartReturns) {
  const { cartItems, getErrors } = props;

  const ticketItems = cartItems.filter((item) => item.buyableItem.buyableType === "ticket");
  const badgeItems = cartItems.filter((item) => item.buyableItem.buyableType === "badge");

  const formatPrice = (price: number) => {
    if (price === 0) {
      return "Gratuit";
    }
    return `${price.toFixed(2)}€`;
  };

  const renderEmptyCart = () => {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <BagIcon width={24} height={24} fill={"#98A2B3"} />
        <p className="text-gray-500">Aucun article dans votre panier</p>
      </div>
    );
  };

  const renderCartItems = () => {
    return (
      <div className="flex-1 overflow-auto p-4">
        {ticketItems.map((item) => (
          <Item
            key={item.key}
            item={item}
            errors={getErrors(item.key)}
            formatPrice={formatPrice}
            {...props}
          />
        ))}
        {badgeItems.map((item) => (
          <Item
            key={item.key}
            item={item}
            errors={getErrors(item.key)}
            formatPrice={formatPrice}
            {...props}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      {ticketItems.length === 0 && badgeItems.length === 0 ? renderEmptyCart() : renderCartItems()}
    </>
  );
}

type ItemProps = {
  item: LocalCartItem;
  errors: string[];
  formatPrice: (price: number) => string;
} & UseSyncedCartReturns;

function Item({
  item,
  errors,
  formatPrice,
  incrementItem,
  decrementItem,
  removeItem,
  getItemFinalPricing,
  cart,
}: ItemProps) {
  const pricingData = item.pricing;
  const basePrice = centsToPrice(pricingData.price?.priceTTC ?? 0);
  const finalPricing = getItemFinalPricing(item.key);
  const quantity = finalPricing?.quantity ?? 1;
  const degressiveDiscountPerUnit = (finalPricing?.degressiveDiscountAmount ?? 0) / quantity;
  const priceAfterDegressive = centsToPrice(
    (pricingData.price?.priceTTC ?? 0) - degressiveDiscountPerUnit
  );
  const hasDegressiveDiscount = degressiveDiscountPerUnit > 0;

  const buyableName = item.buyableItem.ticketType?.name ?? item.buyableItem.badgeType?.name ?? "";
  const quantityLeft =
    item.buyableItem.ticketType?.quantityLeft ?? item.buyableItem.badgeType?.quantityLeft ?? null;
  const groupMinSize =
    item.buyableItem.ticketType?.groupMinSize ?? item.buyableItem.badgeType?.groupMinSize ?? null;

  const hasError = errors.length > 0;
  const errorMessage = errors[0] ?? null;

  const decreaseDisabled =
    item.quantity <= 0 ||
    (groupMinSize !== null && groupMinSize >= item.quantity) ||
    errorMessage === TICKET_NOT_AVAILABLE_YET ||
    errorMessage === TICKET_NOT_AVAILABLE_ANYMORE ||
    // A refused item can only be removed, its quantity is meaningless.
    errorMessage === TICKET_NOT_ELIGIBLE;

  // Same budget as the server's basket validation, the purchased tickets already deducted.
  const remainingAllowance = item.buyableItem.ticketType
    ? getTicketPurchaseAllowance(item.buyableItem.ticketType, cart?.buyer).remaining
    : null;
  const increaseDisabled =
    hasError ||
    (quantityLeft !== null && quantityLeft <= item.quantity) ||
    (remainingAllowance !== null && item.quantity >= remainingAllowance);

  return (
    <div
      key={item.key}
      className="mb-4 grid grid-cols-[1fr_max-content] items-center justify-between gap-y-2 rounded-lg border bg-white px-4 py-3"
    >
      <div className="flex flex-col">
        <div className="flex flex-col gap-1">
          <p className="text-base font-bold">{buyableName}</p>
        </div>
        <div className="pt-3">
          <div className="flex items-center gap-2">
            <button
              aria-label={`Retirer un ${buyableName}`}
              onClick={() => decrementItem(item.key)}
              disabled={decreaseDisabled}
            >
              <MinusCircleIcon
                width={26}
                height={26}
                fill={decreaseDisabled ? "#98A2B3" : "#287BB9"}
              />
            </button>
            <span className="text-center text-base font-bold">{item.quantity}</span>
            <button
              aria-label={`Ajouter un ${buyableName}`}
              onClick={() => incrementItem(item.key)}
              disabled={increaseDisabled}
            >
              <PlusCircleIcon
                width={24}
                height={24}
                fill={increaseDisabled ? "#98A2B3" : "#287BB9"}
              />
            </button>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end">
        <button
          className="mb-4"
          aria-label={`Supprimer ${buyableName}`}
          onClick={() => removeItem(item.key)}
        >
          <TrashIcon width={24} height={24} fill={"#F04438"} />
        </button>
        <p className="mt-2 font-bold">
          {hasDegressiveDiscount && <span className="mr-2">{formatPrice(priceAfterDegressive)}</span>}
          <span className={cn(hasDegressiveDiscount && "text-xs text-gray-500 line-through")}>
            {formatPrice(basePrice)}
          </span>
        </p>
        {hasDegressiveDiscount && (
          <span className="text-right text-[10px] leading-none text-gray-500">
            Prix du billet suivant après
            <br />
            application de la dégressivité
          </span>
        )}
      </div>
      {errorMessage && (
        <p className="col-span-full text-sm text-red-500">
          {errorMessage}
          {(errorMessage === TICKET_QUANTITY_TOO_LOW ||
            errorMessage === BADGE_QUANTITY_TOO_LOW) && <span> (reste {quantityLeft})</span>}
        </p>
      )}
    </div>
  );
}

export default TicketsCart;
